import { useEffect, useState } from 'react';

import AddTask from '@/components/AddTask';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import TaskItem from '@/components/TaskItem';
import defaultTaskFilters from '@/constants/defaultTaskFilters';
import { defaultTaskSortOption } from '@/constants/taskSortOptions';
import TaskFilterForm from '@/forms/TaskFilterForm';
import type { TaskFilters } from '@/forms/TaskFilterForm/types';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import useQueryString from '@/hooks/useQueryString';
import WorkRepository from '@/repositories/WorkRepository';
import type { ItemParams } from '@/types/item';
import Container from '@/ui/components/Container';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading/Heading';
import Spinner from '@/ui/components/Spinner';

interface Props {
  object_id: string;
  object_name: string;
  contentTypeName: string;
  personId?: string | undefined;
}

const TaskList = ({ object_id, object_name, contentTypeName, personId }: Props) => {
  const [totalPages, setTotalPages] = useState<number>(0);
  const contentTypeModel = contentTypeName.includes('.')
    ? contentTypeName.split('.')[1]
    : contentTypeName;

  const {
    params: queryParams,
    setParams: setQueryParams,
    hasQueryParams
  } = useQueryString<Partial<ItemParams>>();
  const [params, setParams] = useState<ItemParams>((): ItemParams => {
    // Initialize from query params
    if (hasQueryParams) {
      return {
        ...(queryParams as Partial<TaskFilters>),
        page: queryParams.page || '1'
      } as ItemParams;
    }

    // Apply defaults
    return {
      ...defaultTaskFilters,
      page: '1'
    };
  });
  const [sort, setSort] = useState<OptionType<string>>(defaultTaskSortOption);
  const workRepo = WorkRepository();

  const setFilters = (filters: Partial<TaskFilters>) => {
    return setParams({ ...filters, page: '1' });
  };

  useEffect(() => {
    setQueryParams({ ...params, ordering: sort.value });
  }, [params, sort, setQueryParams]);

  const {
    data: itemsResponse,
    isPending: isLoadingItems,
    isSuccess
  } = useAPI({
    queryKey: ['getTasks', object_id, { ...params, ordering: sort.value }],
    queryFn: async () => {
      return await workRepo.getItems({
        personId: personId,
        taskableId: object_id,
        taskableTypeModel: contentTypeModel,
        params: {
          ...params,
          ordering: sort.value
        }
      });
    }
  });

  useEffect(() => {
    if (isSuccess) {
      if (itemsResponse?.data?.page) {
        setParams({ ...params, page: itemsResponse.data.page.toString() });
      }

      if (itemsResponse?.data?.total_pages) {
        setTotalPages(itemsResponse.data.total_pages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, itemsResponse]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    parseInt(params.page, 10),
    totalPages
  );

  useEffect(() => {
    setParams((currentParams) => ({ ...currentParams, page: currentPage.toString() }));
  }, [currentPage]);

  return (
    <Container type="page">
      <PageHeader headingText="Tasks" className="sr-only" />
      <TaskFilterForm
        activeFilters={params}
        setFilters={setFilters}
        setSort={setSort}
        sort={sort}
      />

      <Pagination
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isLoadingItems && <Spinner />}

        {!itemsResponse?.data?.results.length ? (
          <div className="text-center pt-8">
            <Heading text={`No tasks found for ${object_name}`} value={3} />
          </div>
        ) : (
          <ul role="list">
            {itemsResponse?.data?.results.map((item) => (
              <li key={item.id}>
                <TaskItem item={item} />
              </li>
            ))}
          </ul>
        )}
        <div className="text-center p-4">
          <div className="mt-3">
            <AddTask personId={personId} taskableId={object_id} taskableType={contentTypeName} />
          </div>
        </div>
      </Pagination>
    </Container>
  );
};

export default TaskList;
